import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import KinConstructionKit from '../components/KinConstructionKit';
import KinGovernance from '../components/KinGovernance';
import KinFinancialStatement from '../components/KinFinancialStatement';
import KinDraftSummary from '../components/KinDraftSummary';
import KinOrdersView from '../components/KinOrdersView';
import KinMembersMap from '../components/KinMembersMap';
import SupplierSettings from '../components/SupplierSettings';
import KinYourFunds from '../components/KinYourFunds';

// ============================================================
// COMPONENT: CoordinatorDraftsView
// PURPOSE: Shows submitted drafts to Coordinator with Consolidate/Finalize buttons
// START: CoordinatorDraftsView component
// ============================================================
const CoordinatorDraftsView = ({ kinId }) => {
  // --- State ---
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consolidating, setConsolidating] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [draftStatus, setDraftStatus] = useState(null);
  const [startingRevision, setStartingRevision] = useState(false);
  const hasSubmitted = drafts.some(d => d.status === 'submitted');
  const hasConsolidated = drafts.some(d => d.status === 'consolidated');
  const hasRevising = drafts.some(d => d.status === 'revising');
  const hasRevised = drafts.some(d => d.status === 'revised');
  const hasFinalized = drafts.some(d => d.status === 'finalized');
  const [generatingOrders, setGeneratingOrders] = useState(false);

  // --- Effects ---
  useEffect(() => {
    if (kinId) {
      fetchDrafts();
    }
  }, [kinId]);

  // --- API Calls ---
  const fetchDrafts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/member-drafts/kin/${kinId}/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDrafts(data);
      } else {
        const err = await res.json();
        setError(err.error || 'Failed to load drafts');
      }
    } catch (err) {
      console.error('Error fetching drafts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConsolidate = async () => {
    if (!window.confirm('Consolidate all submitted drafts? This will apply group volume pricing to all drafts.')) return;
    
    setConsolidating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/member-drafts/kin/${kinId}/consolidate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        alert(`✅ Consolidated ${data.totalDrafts} drafts successfully!`);
        fetchDrafts();
      } else {
        const err = await res.json();
        alert('❌ Failed to consolidate: ' + (err.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error consolidating:', err);
      alert('❌ Failed to consolidate');
    } finally {
      setConsolidating(false);
    }
  };

const handleStartRevision = async () => {
  if (!window.confirm('Start revision phase? Members will be able to adjust their quantities.')) return;
  
  setStartingRevision(true);
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`/api/member-drafts/kin/${kinId}/start-revision`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (res.ok) {
      const data = await res.json();
      alert(`✅ Revision phase started for ${data.totalDrafts} drafts!`);
      fetchDrafts();
    } else {
      const err = await res.json();
      alert('❌ Failed to start revisions: ' + (err.error || 'Unknown error'));
    }
  } catch (err) {
    console.error('Error starting revisions:', err);
    alert('❌ Failed to start revisions');
  } finally {
    setStartingRevision(false);
  }
};

  const handleFinalize = async () => {
    if (!window.confirm('Finalize all orders? This will create the final version (v3) and close the ordering window.')) return;
    
    setFinalizing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/member-drafts/kin/${kinId}/finalize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        alert(`✅ Finalized ${data.totalDrafts} orders successfully!`);
        fetchDrafts();
      } else {
        const err = await res.json();
        alert('❌ Failed to finalize: ' + (err.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error finalizing:', err);
      alert('❌ Failed to finalize');
    } finally {
      setFinalizing(false);
    }
  };

const handleGenerateOrders = async () => {
  if (!window.confirm('Generate vendor orders from all finalized drafts?')) return;
  
  setGeneratingOrders(true);
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`/api/kin-orders/kin/${kinId}/generate-orders`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (res.ok) {
      const data = await res.json();
      alert(`✅ ${data.message}\n${data.totalVendors} vendors, ${data.totalMembers} members`);
      fetchDrafts();
    } else {
      const err = await res.json();
      alert('❌ Failed to generate orders: ' + (err.error || 'Unknown error'));
    }
  } catch (err) {
    console.error('Error generating orders:', err);
    alert('❌ Failed to generate orders');
  } finally {
    setGeneratingOrders(false);
  }
};

  // --- Loading & Error States ---
  if (loading) {
    return <div className="text-gray-500">Loading member drafts...</div>;
  }

  if (error) {
    return <div className="text-red-600">Error: {error}</div>;
  }

  // --- Render ---
  return (
    <div>
      {/* Header with buttons */}
<div className="flex justify-between items-center mb-4">
  <h3 className="text-lg font-semibold">📋 Member Drafts ({drafts.length})</h3>
  <div className="flex flex-wrap gap-2">
    {hasSubmitted && (
      <button
        onClick={handleConsolidate}
        disabled={consolidating}
        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
      >
        {consolidating ? 'Consolidating...' : '📊 Consolidate'}
      </button>
    )}

    {hasConsolidated && !hasRevising && (
      <button
        onClick={handleStartRevision}
        disabled={startingRevision}
        className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:opacity-50 text-sm"
      >
        {startingRevision ? 'Starting...' : '✏️ Start Revisions'}
      </button>
    )}

    {hasRevised && (
      <button
        onClick={handleFinalize}
        disabled={finalizing}
        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 text-sm"
      >
        {finalizing ? 'Finalizing...' : '✅ Finalize Orders'}
      </button>
    )}

    {hasFinalized && (
      <button
        onClick={handleGenerateOrders}
        disabled={generatingOrders}
        className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 disabled:opacity-50 text-sm"
      >
        {generatingOrders ? 'Generating...' : '📦 Generate Vendor Orders'}
      </button>
    )}
  </div>
</div>

{/* Summary statistics */}
{drafts.length > 0 && (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
      <p className="text-xs text-green-600">Total Retail Value</p>
      <p className="text-lg font-bold text-green-700">
        ${drafts.reduce((sum, d) => {
          const v0 = d.versions?.find(v => v.version === 0);
          return sum + (v0?.total || d.total || 0);
        }, 0).toFixed(2)}
      </p>
    </div>
    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
      <p className="text-xs text-blue-600">Total Member Savings</p>
      <p className="text-lg font-bold text-blue-700">
        ${drafts.reduce((sum, d) => {
          const v1 = d.versions?.find(v => v.version === 1);
          const v0 = d.versions?.find(v => v.version === 0);
          return sum + ((v0?.total || 0) - (v1?.total || 0));
        }, 0).toFixed(2)}
      </p>
    </div>
    <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
      <p className="text-xs text-purple-600">Total Group Savings</p>
      <p className="text-lg font-bold text-purple-700">
        ${drafts.reduce((sum, d) => {
          const v1 = d.versions?.find(v => v.version === 1);
          const v2 = d.versions?.find(v => v.version === 2);
          return sum + ((v1?.total || 0) - (v2?.total || 0));
        }, 0).toFixed(2)}
      </p>
    </div>
  </div>
)}

      {/* Draft list */}
      {drafts.length === 0 ? (
        <p className="text-gray-500">No submitted drafts yet. Members will appear here when they submit their orders.</p>
      ) : (
        <div className="space-y-4">
          {drafts.map(draft => {
            // Determine version status
            let versionLabel = 'v1';
            let versionColor = 'text-blue-600';
            if (draft.status === 'consolidated' || draft.status === 'revising') {
              versionLabel = 'v2 (Consolidated)';
              versionColor = 'text-purple-600';
            }
            if (draft.status === 'finalized') {
              versionLabel = 'v3 (Final)';
              versionColor = 'text-green-600';
            }
            
            return (
              <div key={draft._id} className="border rounded-lg p-4 bg-white shadow-sm">
                {/* Member info */}
                <div className="flex flex-wrap justify-between items-start gap-2">
                  <div>
                    <p className="font-medium">{draft.memberId?.name || 'Unknown Member'}</p>
                    <p className="text-sm text-gray-500">
                      {draft.items.length} items • ${draft.total?.toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-400">
                      Submitted {new Date(draft.submittedAt).toLocaleDateString()}
                    </p>
{/* Get savings from the latest version */}
{(() => {
  const latestVersion = draft.versions?.[draft.versions.length - 1];
  const savings = latestVersion?.savings || 0;
  const savingsPercent = latestVersion?.savingsPercent || 0;
  if (savings > 0) {
    return (
      <p className="text-xs text-green-600">
        💰 Savings: ${savings.toFixed(2)} ({savingsPercent.toFixed(1)}%)
      </p>
    );
  }
  return null;
})()}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`text-xs font-medium ${versionColor}`}>
                      {versionLabel}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      draft.status === 'submitted' ? 'bg-yellow-100 text-yellow-800' :
                      draft.status === 'consolidated' ? 'bg-purple-100 text-purple-800' :
                      draft.status === 'revising' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {draft.status === 'submitted' && '⏳ Submitted'}
                      {draft.status === 'consolidated' && '📊 Consolidated'}
                      {draft.status === 'revising' && '✏️ Revising'}
                      {draft.status === 'finalized' && '✅ Finalized'}
                    </span>
                  </div>
                </div>
                
                {/* Items */}
                <div className="mt-2 flex flex-wrap gap-1">
                  {draft.items.slice(0, 5).map(item => (
                    <span key={item._id} className="bg-gray-100 px-2 py-0.5 rounded-full text-xs">
                      {item.productName} ×{item.quantity}
                    </span>
                  ))}
                  {draft.items.length > 5 && (
                    <span className="text-gray-400 text-xs">+{draft.items.length - 5} more</span>
                  )}
                </div>
                
{/* Version history */}
{draft.versions && draft.versions.length > 1 && (
  <div className="mt-2 pt-2 border-t border-gray-100">
    <div className="flex gap-3 text-xs text-gray-400">
      {draft.versions.map((v, i) => (
        <span key={i}>
          {v.label}: ${v.total?.toFixed(2)}
          {v.savings > 0 && ` (save $${v.savings.toFixed(2)}, ${v.savingsPercent?.toFixed(1)}%)`}
        </span>
      ))}
    </div>
  </div>
)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
// ============================================================
// END: CoordinatorDraftsView component
// ============================================================

// ============================================================
// COMPONENT: MemberStatusView
// PURPOSE: Shows all Kin members with draft submission status and training status
// START: MemberStatusView component
// ============================================================
const MemberStatusView = ({ kinId, isCoordinator, user }) => {
  // --- State ---
  const [members, setMembers] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(null);

  // --- Effects ---
  useEffect(() => {
    if (kinId) {
      fetchData();
    }
  }, [kinId]);

  // --- API Calls ---
  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const membersRes = await fetch(`/api/groups/${kinId}/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const membersData = await membersRes.json();
      
      // Bulk fetch course status for all members
      const userIds = membersData.map(m => m._id);
      const statusRes = await fetch(`/api/education/users/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ userIds })
      });
      
      let courseStatus = {};
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        courseStatus = statusData.users || {};
      }
      
      // Merge course status into members
      const membersWithStatus = membersData.map(member => ({
        ...member,
        ...courseStatus[member._id],
        allRequiredComplete: courseStatus[member._id]?.allRequiredComplete || false
      }));
      
      setMembers(membersWithStatus);
      
      if (isCoordinator) {
        const draftsRes = await fetch(`/api/member-drafts/kin/${kinId}/submitted`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const draftsData = await draftsRes.json();
        setDrafts(Array.isArray(draftsData) ? draftsData : []);
      } else {
        try {
          const myDraftRes = await fetch(`/api/member-drafts/my/${kinId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (myDraftRes.ok) {
            const myDraftData = await myDraftRes.json();
            if (myDraftData.draft && myDraftData.draft.status === 'submitted') {
              setDrafts([{
                memberId: user?._id,
                status: 'submitted'
              }]);
            }
          }
        } catch (err) {
          console.log('No draft found for member');
        }
      }
    } catch (err) {
      console.error('Error fetching member data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendReminder = async (memberId) => {
    if (!window.confirm('Send a reminder to this member to submit their draft?')) return;
    
    setSendingReminder(memberId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/kin/send-reminder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ kinId, memberId })
      });
      
      if (res.ok) {
        alert('✅ Reminder sent successfully!');
      } else {
        const err = await res.json();
        alert('❌ Failed to send reminder: ' + (err.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error sending reminder:', err);
      alert('❌ Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  // --- Loading & Error States ---
  if (loading) {
    return <div className="text-gray-500">Loading member status...</div>;
  }

  if (error) {
    return <div className="text-red-600">Error: {error}</div>;
  }

  // --- Calculate stats ---
  const submittedMemberIds = new Set();
  drafts.forEach(d => {
    if (d.memberId) {
      const id = d.memberId._id?.toString() || d.memberId.toString();
      submittedMemberIds.add(id);
    }
  });

  const totalMembers = members.length;
  const submittedCount = submittedMemberIds.size;
  const pendingCount = totalMembers - submittedCount;

  // --- Render ---
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">
          👥 Member Status ({totalMembers} members)
        </h3>
        <span className="text-sm text-gray-500">
          ✅ {submittedCount} submitted • ⏳ {pendingCount} pending
        </span>
      </div>

      <div className="space-y-2">
        {members.map(member => {
          const memberId = member._id?.toString() || member.userId?.toString();
          const hasSubmitted = submittedMemberIds.has(memberId);
          const isCurrentUser = memberId === user?._id?.toString();
          const memberName = member.name || member.userId?.name || member.email || 'Unknown';
          const memberEmail = member.email || member.userId?.email;
          
          // Determine status icon and label
          let statusIcon = '⏳';
          let statusLabel = 'Pending';
          let statusColor = 'text-gray-400';
          let trainingWarning = false;
          
          if (hasSubmitted) {
            statusIcon = '✅';
            statusLabel = 'Submitted';
            statusColor = 'text-green-600';
          } else if (!member.allRequiredComplete) {
            statusIcon = '📚';
            statusLabel = 'Training Required';
            statusColor = 'text-yellow-600';
            trainingWarning = true;
          } else {
            statusIcon = '⏳';
            statusLabel = 'Pending';
            statusColor = 'text-gray-400';
          }
          
          return (
            <div key={memberId || Math.random()} className="flex flex-wrap items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
              <div className="flex items-center gap-3">
                <span className="text-lg">{statusIcon}</span>
                <div>
                  <span className="font-medium">{memberName}</span>
                  {isCurrentUser && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">You</span>
                  )}
                  {member.role === 'leader' && (
                    <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">🌟 Coordinator</span>
                  )}
                  {trainingWarning && (
                    <span className="ml-2 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">⚠️ Training Required</span>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <span className={`text-sm ${statusColor}`}>
                  {statusIcon} {statusLabel}
                </span>
                
                {isCoordinator && !hasSubmitted && !isCurrentUser && (
                  <button
                    onClick={() => sendReminder(memberId)}
                    disabled={sendingReminder === memberId}
                    className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {sendingReminder === memberId ? 'Sending...' : '📧 Remind'}
                  </button>
                )}
                
                {isCoordinator && memberEmail && (
                  <a
                    href={`mailto:${memberEmail}`}
                    className="text-sm text-gray-500 hover:text-gray-700"
                    title="Send email"
                  >
                    📧
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
// ============================================================
// END: MemberStatusView component
// ============================================================

// Course progress component for Kin members
const KinCourseProgress = ({ userId, onComplete }) => {
  const [courses, setCourses] = useState([
    { id: 'security', label: '🔒 Security & Privacy', completed: false, path: '/learn/security-privacy-course' },
    { id: 'curious', label: '🔍 Curious Course', completed: false, path: '/learn/curious-module-1-welcome' },
    { id: 'buyer', label: '🛒 Buyer Course', completed: false, path: '/learn/buyer-module-1-welcome' },
    { id: 'kin', label: '👥 Kin Course', completed: false, path: '/learn/kin-module-1-what-is-a-kin' }
  ]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourseProgress();
  }, [userId]);

const fetchCourseProgress = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/education/user/${userId}/status`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (response.ok) {
      const data = await response.json();
      setCourses(courses.map(c => ({
        ...c,
        completed: data.courses[c.id] || false
      })));
      if (data.allRequiredComplete && onComplete) onComplete();
    }
  } catch (err) {
    console.error('Error fetching course progress:', err);
  } finally {
    setLoading(false);
  }
};

  const totalCompleted = courses.filter(c => c.completed).length;
  const totalCourses = courses.length;

  if (loading) {
    return <div className="text-sm text-gray-500">Loading course progress...</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-700">
          Progress: {totalCompleted}/{totalCourses} courses complete
        </span>
        {totalCompleted === totalCourses && (
          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
            ✅ All courses complete!
          </span>
        )}
      </div>

      <div className="space-y-2">
        {courses.map(course => (
          <Link
            key={course.id}
            to={course.path}
            className={`flex items-center justify-between p-3 rounded-lg border transition ${
              course.completed
                ? 'bg-green-50 border-green-200 hover:bg-green-100'
                : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{course.completed ? '✅' : '📚'}</span>
              <span className={`text-sm ${course.completed ? 'text-green-700' : 'text-gray-700'}`}>
                {course.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {course.completed ? (
                <span className="text-xs bg-green-200 text-green-700 px-2 py-1 rounded-full">
                  Completed
                </span>
              ) : (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                  Start →
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

const KinDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kinData, setKinData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showInvitePrompt, setShowInvitePrompt] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [coursesComplete, setCoursesComplete] = useState(false);
  const [hasSubmittedDraft, setHasSubmittedDraft] = useState(false);
  const [draftStatus, setDraftStatus] = useState(null);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
    }
  };

  const checkSubmittedDraft = async () => {
    try {
      const token = localStorage.getItem('token');
      const kinId = kinData?._id || user?.activeKinId;
      if (!kinId) return;
      const res = await fetch(`/api/member-drafts/my/${kinId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.draft && data.draft.status === 'submitted') {
          setHasSubmittedDraft(true);
        }
      }
    } catch (err) {
      console.error('Error checking draft status:', err);
    }
  };

const checkDraftStatus = async () => {
  try {
    const token = localStorage.getItem('token');
    const kinId = kinData?._id || user?.activeKinId;
    if (!kinId) return;
    const res = await fetch(`/api/member-drafts/my/${kinId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) {
      const data = await res.json();
      if (data.draft) {
        setDraftStatus(data.draft.status);
        if (data.draft.status === 'submitted' || data.draft.status === 'finalized') {
          setHasSubmittedDraft(true);
        }
      }
    }
  } catch (err) {
    console.error('Error checking draft status:', err);
  }
};

  const hasKin = user?.activeKinId || kinData?._id;

  useEffect(() => {
    if (user?.activeKinId) {
      fetchKinData(user.activeKinId);
    } else {
      setLoading(false);
      setKinData(null);
    }
  }, [user]);

  useEffect(() => {
    if (user && kinData) {
      const checkCourses = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch('/api/education/progress/courses', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            const allComplete = ['security', 'curious', 'buyer', 'kin'].every(c => data[c] === true);
            setCoursesComplete(allComplete);
          }
        } catch (err) {
          console.error('Error checking courses:', err);
        }
      };
      checkCourses();
      checkSubmittedDraft();
      checkDraftStatus();
    }
  }, [user, kinData]);

  const fetchKinData = async (kinId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/groups/${kinId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 404) {
          setKinData(null);
          setError('Your Kin could not be found. You may need to create a new one.');
        } else {
          throw new Error('Failed to fetch Kin data');
        }
        return;
      }

      const data = await response.json();
      console.log('📊 Kin data received:', data);
      setKinData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching Kin data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isCoordinator = (() => {
    if (!kinData || !user) return false;

    if (kinData.members && Array.isArray(kinData.members)) {
      return kinData.members.some(m => {
        const memberId = m.userId?._id?.toString() || m.userId?.toString() || m.userId;
        const userIdStr = user._id?.toString();
        return memberId === userIdStr && m.role === 'leader';
      });
    }

    if (user.activeKinId?.toString() === kinData._id?.toString()) {
      if (!kinData.members || kinData.members.length === 0) {
        return true;
      }
    }

    return false;
  })();

  console.log('🔍 isCoordinator check:', { isCoordinator, userId: user?._id, kinDataMembers: kinData?.members });

  if (!hasKin && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="text-6xl mb-4">👥</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">You're not part of a Kin yet</h1>
            <p className="text-gray-600 mb-6">
              Join a Kin or start your own to start saving on food together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/kin-create"
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition"
              >
                🚀 Start a Kin
              </Link>
              <Link
                to="/kin-join"
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition"
              >
                🔗 Join a Kin
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="text-2xl">⏳</div>
            <p className="text-gray-600 mt-2">Loading your Kin...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-red-600 mb-2">Error loading Kin</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link
              to="/kin/create"
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition"
            >
              Create a New Kin
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (kinData?.status === 'pending_setup') {
    return (
      <KinConstructionKit
        kinId={kinData._id}
        onComplete={() => {
          fetchKinData(kinData._id);
        }}
      />
    );
  }

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    if (!inviteEmail) {
      alert('Please enter an email address');
      return;
    }

    setSendingInvite(true);
    try {
      const token = localStorage.getItem('token');
      const kinId = user?.activeKinId;
      if (!kinId) {
        alert('No Kin found. Please refresh and try again.');
        return;
      }

      const response = await fetch(`/api/groups/${kinId}/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: inviteEmail,
          message: inviteMessage.trim() || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send invitation');
      }

      alert(`✅ Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteMessage('');
      setShowInvitePrompt(false);
    } catch (error) {
      console.error('Error sending invitation:', error);
      alert(`❌ ${error.message || 'Failed to send invitation'}`);
    } finally {
      setSendingInvite(false);
    }
  };

  const generateInviteCode = () => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    const inviteLink = `${window.location.origin}/kin/join?code=${code}`;
    return inviteLink;
  };

  const inviteCode = kinData?.inviteCode || generateInviteCode();

const tabs = [
  { id: 'dashboard', label: '📊 Dashboard' },
  { id: 'members', label: '👥 Members' },
  { id: 'map', label: '🗺️ Map' },
  { id: 'yourfunds', label: '💵 Your Kin Funds' },
  ...(isCoordinator ? [{ id: 'drafts', label: '📝 Drafts' }] : []),
  { id: 'orders', label: '📦 Orders' },
  { id: 'financial', label: '💰 Financial' },
  { id: 'governance', label: '🗳️ Governance' },
  ...(isCoordinator ? [
  { id: 'invitations', label: '📨 Invitations' },
] : [])
]; 

  if (isCoordinator) {
    tabs.push({ id: 'settings', label: '⚙️ Settings' });
    tabs.push({ id: 'suppliers', label: '🏪 Suppliers' });
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {kinData?.name || 'My Kin'}
            </h1>
            <p className="text-gray-600">
              {kinData?.members?.length || 0} members •
              {isCoordinator ? ' 🌟 Coordinator' : ' 👥 Member'}
            </p>
            {isCoordinator && (
              <span className="inline-block mt-1 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                Coordinator - You can manage this Kin
              </span>
            )}
          </div>
          {isCoordinator && (
            <div className="flex gap-2">
              <Link
                to={`/kin-admin/${kinData?._id || user?.activeKinId}`}
                className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition"
              >
                ⚙️ Kin Admin
              </Link>
              <button
                onClick={() => setShowInvitePrompt(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
              >
                ✉️ Invite Members
              </button>
            </div>
          )}
        </div>

        {/* Invite Modal */}
        {showInvitePrompt && isCoordinator && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Invite Member to Kin</h2>
                <button
                  onClick={() => setShowInvitePrompt(false)}
                  className="text-gray-500 hover:text-gray-700 transition"
                >
                  ✕
                </button>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">📋 Share this invite link:</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={inviteCode}
                    readOnly
                    className="flex-1 text-sm bg-white border rounded px-2 py-1"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard?.writeText(inviteCode);
                      alert('Invite link copied to clipboard!');
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    📋 Copy
                  </button>
                </div>
              </div>

              <form onSubmit={handleInviteSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="friend@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Personal Message (optional)
                  </label>
                  <textarea
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    placeholder="Hey, I'd love you to join our Kin..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={sendingInvite}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                  >
                    {sendingInvite ? 'Sending...' : 'Send Invite'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowInvitePrompt(false)}
                    className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {!coursesComplete && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">📚</span>
              <div className="flex-1">
                <p className="font-semibold text-yellow-800">Complete your onboarding!</p>
                <p className="text-sm text-yellow-700 mb-3">
                  You're a Kin member! 🎉 Complete these courses to unlock ordering:
                </p>
                <KinCourseProgress
                  userId={user?._id}
                  onComplete={() => {
                    setCoursesComplete(true);
                    fetchUserData();
                  }}
                />
              </div>
            </div>
          </div>
        )}

{coursesComplete && !hasSubmittedDraft && (
  <Link
    to={`/member-draft/${kinData?._id || user?.activeKinId}`}
    className="inline-block mt-2 text-sm bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
  >
    {draftStatus === 'consolidated' || draftStatus === 'revising' 
      ? '✏️ Revise Draft Order →' 
      : '📝 Create Draft Order →'}
  </Link>
)}

        {coursesComplete && hasSubmittedDraft && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">✅</span>
              <div>
                <p className="font-semibold text-blue-800">Draft Submitted!</p>
                <p className="text-sm text-blue-700">
                  You've already submitted your draft. Waiting for the coordinator to consolidate orders.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg transition ${
                activeTab === tab.id
                  ? 'bg-green-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-md p-6">
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Members</div>
                  <div className="text-2xl font-bold text-green-700">
                    {kinData?.members?.length || 0}
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Pending Orders</div>
                  <div className="text-2xl font-bold text-blue-700">
                    {kinData?.pendingOrders || 0}
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Total Savings</div>
                  <div className="text-2xl font-bold text-purple-700">
                    ${kinData?.totalSavings?.toFixed(2) || '0.00'}
                  </div>
                </div>
              </div>

              {isCoordinator && (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    💡 As Coordinator, you can manage drafts, invite members, and oversee orders.
                  </p>
                </div>
              )}

              {!isCoordinator && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    👥 You are a member of this Kin. Your coordinator is organizing orders for the group.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'members' && (
            <MemberStatusView 
              kinId={kinData?._id || user?.activeKinId} 
              isCoordinator={isCoordinator}
              user={user}
            />
          )}
          {activeTab === 'yourfunds' && (
            <KinYourFunds kinId={kinData?._id || user?.activeKinId} />
          )}

{activeTab === 'map' && (
  <div className="bg-white rounded-lg shadow-md p-6">
    <h2 className="text-xl font-bold mb-4">🗺️ Kin Members Map</h2>
    <p className="text-gray-600 mb-4">
      See where Kin members are located around the coordinator. The yellow circle shows the Kin's service radius.
    </p>
    <KinMembersMap 
      kinId={kinData?._id || user?.activeKinId} 
      kinName={kinData?.name} 
    />
  </div>
)}

          {activeTab === 'drafts' && (
            isCoordinator ? (
              <CoordinatorDraftsView kinId={kinData?._id || user?.activeKinId} />
            ) : (
              <KinDraftSummary groupId={kinData?._id || user?.activeKinId} isCoordinator={false} />
            )
          )}

          {activeTab === 'orders' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Orders</h3>
              <KinOrdersView kinId={kinData?._id || user?.activeKinId} isCoordinator={isCoordinator} />
            </div>
          )}

          {activeTab === 'financial' && (
            <KinFinancialStatement kinId={kinData?._id || user?.activeKinId} />
          )}

          {activeTab === 'governance' && (
            <KinGovernance kinId={kinData?._id || user?.activeKinId} isCoordinator={isCoordinator} />
          )}

          {activeTab === 'invitations' && isCoordinator && (
            <div>
              <h3 className="text-lg font-semibold mb-4">📨 Pending Invitations</h3>
              <div className="space-y-2">
                <p className="text-gray-600">Invitations will appear here once you send them.</p>
              </div>
            </div>
          )}

         {activeTab === 'settings' && isCoordinator && (
            <div>
              <h3 className="text-lg font-semibold mb-4">⚙️ Kin Settings</h3>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-2">Invite Code</h4>
                  <div className="flex items-center gap-2">
                    <code className="bg-white px-3 py-1 rounded border">{inviteCode}</code>
                    <button
                      onClick={() => {
                        navigator.clipboard?.writeText(inviteCode);
                        alert('Invite code copied!');
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      📋 Copy
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Share this code with people you want to invite to your Kin.
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-2">Coordinator Compensation</h4>
                  <p className="text-sm text-gray-600">
                    Type: {kinData?.compensationType || 'Volunteer'}
                  </p>
                  <p className="text-sm text-gray-600">
                    Percentage: {kinData?.compensationPercentage || 0}%
                  </p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-2">Community Fund</h4>
                  <p className="text-sm text-gray-600">
                    Goal: ${kinData?.communityFundGoal || 0}
                  </p>
                  <p className="text-sm text-gray-600">
                    Deadline: {kinData?.communityFundDeadline || 0} months
                  </p>
                </div>
              </div>
            </div>
          )}
         {activeTab === 'suppliers' && isCoordinator && (
           <SupplierSettings kinId={kinData?._id || user?.activeKinId} />
         )}
        </div>
      </div>
    </div>
  );
};

export default KinDashboard;
